import React from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import TabbedAdminListPage, { TabbedAdminListPageTab } from 'hooks/TabbedAdminListPage';
import type { AdminListPageConfig, CustomModalDef } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { DenialList } from 'models/DenialList';
import { AICriteriaSet } from 'models/flexible/AICriteriaSet';
import { PERMISSIONS } from 'models/flexible/Profile';
import { aiCriteriaSetsQueryKeys, deleteAICriteriaSet, indexAICriteriaSets } from 'services/api/admin/aiCriteriaSets';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminDenialListsQueryKeys, indexAdminDenialLists } from 'services/api/admin/denialLists';
import { adminIndexGroups } from 'services/api/admin/groups';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import CopyIcon from 'svg/CopyIcon';
import DeactivateIcon from 'svg/DeactivateIcon';
import PencilIcon from 'svg/PencilIcon';
import PlusIcon from 'svg/PlusIcon';
import TrashIcon from 'svg/TrashIcon';

import { buildPromptListConfig } from '../shared/promptListConfig';

import GlobalDenialListSlideout from './GlobalDenialListSlideout';

// ---------------------------------------------------------------------------
// Admission Prompts tab config
// ---------------------------------------------------------------------------

const admissionPromptsConfig = buildPromptListConfig({
  targetClassname: 'Review::Admission',
  title: 'Admissions Prompts',
  columnStorageKey: 'admin.admissionPrompts',
  addButtonLabel: 'Add Admission Prompt',
  archiveObjType: 'Admission Prompt',
  promptLabel: 'Admission',
  addPath: '/admin/admissions/prompts/new',
  editPath: (id) => `/admin/admissions/prompts/${id}/edit`,
  copyPath: (id) => `/admin/admissions/prompts/${id}/edit?copy=true`,
  defaultSort: 'updatedAt desc',
  clientFilterQueryKey: 'admissionPrompts',
});

// ---------------------------------------------------------------------------
// Admission Criteria tab config
// ---------------------------------------------------------------------------

const CRITERIA_INCLUDES = ['group', 'client'] as const;

const admissionCriteriaConfig: AdminListPageConfig<AICriteriaSet> = {
  title: 'Admission Criteria',
  columnStorageKey: 'admin.admissionCriteria',
  queryKey: aiCriteriaSetsQueryKeys.index(),
  fetchFn: (params, signal) => indexAICriteriaSets(params, signal, [...CRITERIA_INCLUDES]),
  includes: CRITERIA_INCLUDES,
  fetchParams: { defaultSort: 'updatedAt desc' },
  showResultCount: true,

  addButton: {
    label: 'Add Admission Criteria',
    permission: 'adminAICriteriaCreate',
    path: '/admin/admissions/criteria/new',
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'criteriaSets', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
    {
      key: 'groupId',
      label: 'Group',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminGroups', 'criteriaSets', 'filter'],
        fetchFn: (params: any, signal: any) => adminIndexGroups(params, signal, ['groupType']),
        params: { sortBy: 'name asc', isProvider: true },
      },
      getOptionLabel: (o: any) => (o?.groupType?.displayName ? `${o.name} (${o.groupType.displayName})` : o?.name),
      getOptionValue: (o: any) => o.id,
    },
  ],

  columns: [
    {
      id: 'client',
      header: 'Client',
      render: (row) => <BoldText>{row.client?.name || '\u2014'}</BoldText>,
    },
    {
      id: 'group',
      header: 'Group',
      render: (row) =>
        row.group?.groupType?.displayName
          ? `${row.group.name} (${row.group.groupType.displayName})`
          : row.group?.name || '\u2014',
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      render: (row) => (row.updatedAt ? simpleDateAtTime(row.updatedAt) : '\u2014'),
    },
  ],

  rowActions: [
    {
      label: 'Edit Criteria',
      icon: <PencilIcon width={18} height={18} />,
      permission: PERMISSIONS.adminAICriteriaEdit,
      onClick: (row, { navigate }) => navigate(`/admin/admissions/criteria/${row.id}/edit`),
    },
    {
      label: 'Duplicate Criteria',
      icon: <CopyIcon width={18} height={18} />,
      permission: PERMISSIONS.adminAICriteriaCreate,
      onClick: (row, { navigate }) => navigate(`/admin/admissions/criteria/${row.id}/edit?copy=true`),
    },
    {
      label: 'Archive Criteria',
      icon: <DeactivateIcon width={18} height={18} style={{ color: colors.accentRed }} />,
      danger: true,
      dividerBefore: true,
      visible: (row, ctx) => !row.archived && ctx.profile.has(PERMISSIONS.adminAICriteriaDelete),
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],
  customModals: {
    archive: {
      render: (row, show, onClose, ctx) => (
        <StandardModal title='Archive Criteria?' colorTheme={ColorThemeName.Danger} onCancel={onClose}>
          <ModalContent>
            <BodySmall>
              <strong>Are you sure you wish to archive this criteria?</strong> Once archived, it will no longer be used
              for {row.group?.name} admissions summary.
            </BodySmall>
          </ModalContent>
          <Actions>
            <Button variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={async () => {
                await deleteAICriteriaSet(row.id);
                onClose();
                ctx.toast('Admission criteria was successfully archived');
                ctx.refetch();
              }}>
              Archive Criteria
            </Button>
          </Actions>
        </StandardModal>
      ),
    },
  } as Record<string, CustomModalDef<AICriteriaSet>>,
};

// ---------------------------------------------------------------------------
// Global Denial Lists tab config
// ---------------------------------------------------------------------------

const DENIAL_LIST_INCLUDES = ['client'] as const;

// Deleting a denial list is wired in OLIO-2462; until then the menu item is disabled for every
// row. Flip this when that lands — archived lists are already deleted, so the archived guard keeps
// the action disabled for them regardless.
const DELETE_DENIAL_LIST_WIRED = false;

// Upload is wired in OLIO-2462 — rendered per design but disabled, and only for admins
// who would own the action (View Only Olio Admins never see it, keeping the tab read-only).
function UploadDenialListButton() {
  const { profile } = useProfile();
  if (!profile.has(PERMISSIONS.adminDenialListCreate)) return null;

  return (
    <Button variant='primary' disabled>
      <PlusIcon width={14} height={14} />
      Upload Denial List
    </Button>
  );
}

// Archived lists are rendered grayed out (but still clickable) when "Show Archived" is on.
const mutedIfArchived = (row: DenialList): React.CSSProperties | undefined =>
  row.archived ? { color: colors.black50 } : undefined;

const globalDenialListsConfig: AdminListPageConfig<DenialList> = {
  title: 'Global Denial Lists',
  columnStorageKey: 'admin.globalDenialLists',
  queryKey: adminDenialListsQueryKeys.index(),
  fetchFn: (params, signal) => indexAdminDenialLists(params, signal),
  includes: DENIAL_LIST_INCLUDES,
  fetchParams: { defaultSort: 'updated_at desc' },
  showResultCount: true,
  headerExtra: <UploadDenialListButton />,

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'denialLists', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
    {
      key: 'archived',
      label: 'Show Archived',
      type: 'checkbox',
      // Unchecked → hide archived (archived=false); checked → omit the filter so archived show too.
      toRequest: (value: any) => (value ? undefined : 'false'),
    },
  ],

  columns: [
    {
      id: 'client',
      header: 'Client Name',
      render: (row, ctx) => (
        <ClickableName style={mutedIfArchived(row)} onClick={() => ctx.openSlideout('items', row)}>
          {row.client?.name || '—'}
        </ClickableName>
      ),
    },
    {
      id: 'patient_count',
      header: 'Patient Count',
      render: (row, ctx) => (
        <PatientCountLink style={mutedIfArchived(row)} onClick={() => ctx.openSlideout('items', row)}>
          {row.patientCount}
        </PatientCountLink>
      ),
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      render: (row) => (
        <span style={mutedIfArchived(row)}>{row.updatedAt ? simpleDateAtTime(row.updatedAt) : '—'}</span>
      ),
    },
  ],

  rowActions: [
    {
      // Delete is wired in OLIO-2462 — the menu item is shown (per design) but disabled, and stays
      // disabled for archived lists. There is intentionally no "Edit List" action.
      label: 'Delete Denial List',
      icon: <TrashIcon width={18} height={18} color={colors.accentRed} />,
      danger: true,
      permission: PERMISSIONS.adminDenialListDelete,
      disabled: (row) => !DELETE_DENIAL_LIST_WIRED || row.archived,
      onClick: () => {},
    },
  ],

  slideouts: {
    items: {
      render: (row, onClose) => <GlobalDenialListSlideout denialList={row} onClose={onClose} />,
    },
  },
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const tabs: TabbedAdminListPageTab[] = [
  {
    label: 'Admission Prompts',
    path: '/admin/admissions/prompts',
    permission: 'adminAIPromptView',
    config: admissionPromptsConfig,
  },
  {
    label: 'Admission Criteria',
    path: '/admin/admissions/criteria',
    permission: 'adminAICriteriaView',
    config: admissionCriteriaConfig,
  },
  {
    label: 'Global Denial Lists',
    path: '/admin/admissions/denial-lists',
    permission: 'adminDenialListView',
    config: globalDenialListsConfig,
  },
];

export default function Admissions() {
  return <TabbedAdminListPage pageTitle='Admissions' tabs={tabs} />;
}

const BoldText = styled.span`
  font-weight: 700;
`;

// Same look as BoldText — only adds a pointer cursor since the name opens the slideout.
const ClickableName = styled(BoldText)`
  cursor: pointer;
`;

const PatientCountLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.primaryBlue};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.75;
  }
`;
